import {
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Statistic,
  TimePicker,
  message,
} from "antd";
import { IEmiDetail, IPaymentTypes, IPaymentTypes_View } from "store/types";
import { Requester, formatURL } from "api/requester";

import { API_END_POINTS } from "api/urls";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IndianRuppeNumberFormattor } from "components/products/product.util.components";
import { LoansActions } from "store/slices/loansSlice";
import { RootState } from "store/store";
import { faIndianRupeeSign } from "@fortawesome/free-solid-svg-icons";
import { mergeDateAndTime } from "components/util-components/dateTimeFormattor";
import { useAppDispatch } from "store/hooks";
import { useSelector } from "react-redux";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface ILoanDashboardAddPaymentFormProps {
  open: boolean;
  close: () => void;
  record: IEmiDetail;
}
const LoanDashboardAddPaymentForm = (
  props: ILoanDashboardAddPaymentFormProps
) => {
  const { t } = useTranslation();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [form] = Form.useForm();
  const { details } = useSelector(
    (state: RootState) => state.loans.loanDashboard
  );
  const dispatch = useAppDispatch();
  const displayAmount = props?.record?.amount;
  const displayType = props?.record?.paymentType || IPaymentTypes.EMI;

  const handleAddPayment = async (formValues: any) => {
    setIsSubmitLoading(true);
    const data = {
      transactionId: formValues?.transactionId,
      paymentMode: formValues?.paymentMode,
      type: displayType,
      amount: displayAmount,
      transactionTime: mergeDateAndTime(
        formValues?.transactionDate,
        formValues?.transactionTime
      ),
    };
    const url = formatURL(API_END_POINTS.LOANS.addPayment, {
      loanId: details?.id,
    });
    const response = await Requester.post(url, data).catch(
      ({ response }) => response
    );
    if (response?.status === 204) {
      message.success(t("Successfully added payment"));
      handleClose();
      dispatch(LoansActions.fetchLoanById(details?.id || ""));
    } else {
      message.error(t("Error while adding payment"));
    }
    setIsSubmitLoading(false);
  };

  const handleClose = () => {
    props?.close();
    form.resetFields();
  };

  return (
    <Modal
      title={t("Add Payment")}
      maskClosable={false}
      onCancel={handleClose}
      open={props?.open}
      okText={t("Submit")}
      onOk={form.submit}
      confirmLoading={isSubmitLoading}
    >
      <Form form={form} layout="vertical" onFinish={handleAddPayment}>
        <Row gutter={12}>
          <Col span={12}>
            <Statistic
              title={t("Amount")}
              prefix={<FontAwesomeIcon icon={faIndianRupeeSign} />}
              value={IndianRuppeNumberFormattor(displayAmount?.toString())}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title={t("Payment Type")}
              value={IPaymentTypes_View[displayType]}
            />
          </Col>
        </Row>
        <Divider />
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t("Please select payment mode"),
                },
              ]}
              name={"paymentMode"}
              label={t("Payment Mode")}
            >
              <Select placeholder={t("Select Payment Mode")}>
                <Select.Option value={"UPI"}>{t("UPI")}</Select.Option>
                <Select.Option value={"CASH"}>{t("Cash")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t("Please enter Transaction ID"),
                },
              ]}
              name={"transactionId"}
              label={t("Transaction ID")}
            >
              <Input placeholder={t("Enter Transaction ID")} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t("Please select transaction date"),
                },
              ]}
              name={"transactionDate"}
              label={t("Date")}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t("Please select transaction time"),
                },
              ]}
              name={"transactionTime"}
              label={t("Time")}
            >
              <TimePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default LoanDashboardAddPaymentForm;
